import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCurrency } from '../../hooks/useCurrency';
import { useSEO, generateOrganizationSchema, generateWebPageSchema } from '../../utils/seo';
import { useState, useEffect, useMemo, useRef } from 'react';
import i18n from '../../i18n';

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const isRTL = i18n.language === 'fa';
  const [scrollY, setScrollY] = useState(0);
  const rafRef = useRef<number>();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Schema.org JSON-LD for homepage
  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Loveish Group',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/3bccd7c51021826c026374f527c8acfa/788c20e3b8cdbfda268b7f12ce62061c.png'
        },
        description: 'LoveishGroup.com is a creative digital brand offering high-quality online services, branding solutions, and modern content creation. We specialize in innovative designs, digital products, and customized solutions that help individuals and businesses grow online.',
        sameAs: [
          'https://facebook.com/loveishgroup',
          'https://instagram.com/loveishgroup',
          'https://twitter.com/loveishgroup'
        ]
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Loveish Group',
        description: 'Premium Food Products and Digital Services',
        publisher: {
          '@id': `${siteUrl}/#organization`
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/products?search={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/#webpage`,
        url: siteUrl,
        name: 'Loveish Group |  Premium Food Products',
        description: 'LoveishGroup.com is a creative digital brand offering high-quality online services, branding solutions, and modern content creation. We specialize in innovative designs, digital products, and customized solutions that help individuals and businesses grow online.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        about: {
          '@id': `${siteUrl}/#organization`
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: `${siteUrl}/og-image.jpg`
        }
      }
    ]
  };

  useSEO({
    title: 'Loveish Group |  Premium Food Products',
    description: 'LoveishGroup.com is a creative digital brand offering high-quality online services, branding solutions, and modern content creation. We specialize in innovative designs, digital products, and customized solutions that help individuals and businesses grow online.',
    keywords: 'Loveish Group, digital services, branding tools, food products online, buy food, premium items, online shop, creative solutions, fresh food, digital products, dried fruit',
    canonical: siteUrl,
    ogType: 'website',
    ogImage: `${siteUrl}/og-image.jpg`,
    schema: homeSchema
  });

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/products?category=${encodeURIComponent(categoryName)}`);
  };

  const categories = [
    {
      name: t('nuts'),
      description: 'Premium quality nuts',
      count: '15+ Products',
      icon: 'ri-plant-line',
      image: 'https://public.readdy.ai/ai/img_res/abe8c48c74d55f560af5d497a956c928.jpg'
    },
    {
      name: t('driedFruits'),
      description: 'Naturally dried fruits',
      count: '12+ Products',
      icon: 'ri-apple-line',
      image: 'https://readdy.ai/api/search-image?query=Colorful%20assorted%20dried%20fruits%20mango%20slices%20peaches%20plums%20figs%20apricots%20arranged%20beautifully%20on%20white%20ceramic%20plate%20clean%20background%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20style%20premium%20quality%20vibrant%20colors&width=400&height=400&seq=categorydriedunique2025b&orientation=squarish'
    },
    {
      name: t('sweets'),
      description: 'Traditional Persian sweets',
      count: '10+ Products',
      icon: 'ri-cake-3-line',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20sweets%20gaz%20sohan%20pashmak%20latifeh%20arranged%20elegantly%20on%20white%20marble%20surface%20with%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20composition%20premium%20quality%20Middle%20Eastern%20confectionery%20golden%20tones&width=400&height=400&seq=categorysweetsunique2025c&orientation=squarish'
    },
    {
      name: t('pickles'),
      description: 'Homemade pickles',
      count: '8+ Products',
      icon: 'ri-leaf-line',
      image: 'https://readdy.ai/api/search-image?query=Artisan%20pickled%20vegetables%20olives%20pearl%20garlic%20in%20elegant%20glass%20jars%20on%20clean%20white%20background%20with%20fresh%20herbs%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20style%20premium%20quality%20preserved%20foods&width=400&height=400&seq=categorypicklesunique2025d&orientation=squarish'
    },
    {
      name: t('jams'),
      description: 'Artisanal jams & honey',
      count: '6+ Products',
      icon: 'ri-drop-line',
      image: 'https://readdy.ai/api/search-image?query=Premium%20fruit%20jams%20strawberry%20carrot%20blackcurrant%20honey%20in%20elegant%20glass%20jars%20with%20fresh%20fruits%20on%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20composition%20high%20quality%20preserves%20bright%20colors&width=400&height=400&seq=categoryjamsunique2025e&orientation=squarish'
    },
    {
      name: t('specialGifts'),
      description: 'Curated gift sets',
      count: '5+ Products',
      icon: 'ri-gift-line',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20Persian%20gift%20boxes%20with%20assorted%20nuts%20dried%20fruits%20and%20sweets%20elegantly%20arranged%20premium%20packaging%20sophisticated%20presentation%20warm%20lighting%20high-end%20commercial%20photography&width=400&height=300&seq=homecat006&orientation=landscape'
    }
  ];

  const productCategories = [
    {
      id: 'nuts',
      title: t('nuts'),
      name: t('nuts'),
      description: t('nutsDesc'),
      image: 'https://readdy.ai/api/search-image?query=Premium%20mixed%20nuts%20walnuts%20almonds%20pistachios%20hazelnuts%20in%20elegant%20wooden%20bowls%20on%20marble%20surface%20with%20soft%20natural%20lighting%20minimalist%20food%20photography%20style%20warm%20golden%20tones%20high%20quality%20commercial%20product%20shot&width=400&height=300&seq=homeprodcat001&orientation=landscape',
      items: [t('walnuts'), t('almonds'), t('pistachios'), t('peanuts')]
    },
    {
      id: 'driedFruits',
      title: t('driedFruits'),
      name: t('driedFruits'),
      description: t('driedFruitsDesc'),
      image: 'https://readdy.ai/api/search-image?query=Colorful%20assorted%20dried%20fruits%20mango%20peaches%20plums%20apples%20bananas%20strawberries%20arranged%20beautifully%20on%20white%20marble%20background%20vibrant%20natural%20colors%20professional%20food%20photography%20clean%20minimalist%20style&width=400&height=300&seq=homeprodcat002&orientation=landscape',
      items: [t('mangoLeaves'), t('peaches'), t('plums'), t('strawberries')]
    },
    {
      id: 'sweets',
      title: t('sweets'),
      name: t('sweets'),
      description: t('sweetsDesc'),
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20sweets%20gaz%20sohan%20latifeh%20kalampeh%20pashmak%20arranged%20on%20ornate%20silver%20tray%20with%20decorative%20patterns%20warm%20golden%20lighting%20authentic%20Middle%20Eastern%20confectionery%20display&width=400&height=300&seq=homeprodcat003&orientation=landscape',
      items: [t('gaz'), t('sohan'), t('latifeh'), t('pashmak')]
    },
    {
      id: 'pickles',
      title: t('pickles'),
      name: t('pickles'),
      description: t('picklesDesc'),
      image: 'https://readdy.ai/api/search-image?query=Variety%20of%20pickled%20vegetables%20and%20olives%20in%20glass%20jars%20with%20fresh%20herbs%20rustic%20wooden%20background%20natural%20lighting%20artisanal%20homemade%20preserves%20style%20clean%20and%20appetizing%20presentation&width=400&height=300&seq=homeprodcat004&orientation=landscape',
      items: [t('lyte'), t('pearlGarlic'), t('olives'), t('cultivatedOlives')]
    },
    {
      id: 'jams',
      title: t('jams'),
      name: t('jams'),
      description: t('jamsDesc'),
      image: 'https://readdy.ai/api/search-image?query=Homemade%20fruit%20jams%20strawberry%20carrot%20blackcurrant%20in%20glass%20jars%20with%20fresh%20fruits%20around%20bright%20natural%20lighting%20rustic%20kitchen%20setting%20artisanal%20preserves%20photography%20style&width=400&height=300&seq=homeprodcat005&orientation=landscape',
      items: [t('strawberryJam'), t('carrotJam'), t('blackcurrantJam'), t('honey')]
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      location: 'New York, USA',
      review: 'The walnuts are absolutely amazing! Fresh, crunchy, and perfectly packaged. I\'ve been ordering from Loveish for months now and the quality never disappoints.',
      product: t('walnuts'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20woman%20customer%20in%20her%2030s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar001&orientation=squarish'
    },
    {
      id: 2,
      name: 'Michael Chen',
      location: 'Los Angeles, USA',
      review: 'Best Persian sweets I\'ve ever tasted! The Gaz is authentic and delicious. Reminds me of my travels to Iran. Fast shipping and excellent customer service.',
      product: t('gaz'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20man%20customer%20in%20his%2040s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar002&orientation=squarish'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      location: 'Chicago, USA',
      review: 'The dried fruits are so flavorful and natural. No added sugar, just pure goodness. My kids love them as healthy snacks. Highly recommend!',
      product: t('mangoLeaves'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20woman%20customer%20in%20her%2020s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar003&orientation=squarish'
    },
    {
      id: 4,
      name: 'David Thompson',
      location: 'Houston, USA',
      review: 'The honey is pure and delicious. You can taste the quality in every spoonful. Perfect for my morning tea. Will definitely order again!',
      product: t('honey'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20man%20customer%20in%20his%2050s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar004&orientation=squarish'
    },
    {
      id: 5,
      name: 'Lisa Anderson',
      location: 'Miami, USA',
      review: 'Ordered the gift set for my parents and they absolutely loved it! Beautiful packaging and premium quality products. Perfect for special occasions.',
      product: t('specialGifts'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20woman%20customer%20in%20her%2030s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar005&orientation=squarish'
    },
    {
      id: 6,
      name: 'James Wilson',
      location: 'Seattle, USA',
      review: 'The pickles are fantastic! Authentic taste and great variety. The pearl garlic is my favorite. Great addition to any meal.',
      product: t('pearlGarlic'),
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20happy%20smiling%20man%20customer%20in%20his%2030s%20with%20natural%20lighting%20clean%20background&width=100&height=100&seq=avatar006&orientation=squarish'
    }
  ];

  const trustBadges = [
    {
      icon: 'ri-shield-check-line',
      title: '100% Authentic',
      description: 'Guaranteed Quality',
      color: 'bg-gradient-to-br from-emerald-500 to-teal-500'
    },
    {
      icon: 'ri-truck-line',
      title: 'Fast Delivery',
      description: 'Worldwide Shipping',
      color: 'bg-gradient-to-br from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-arrow-go-back-line',
      title: 'Easy Returns',
      description: '30-Day Guarantee',
      color: 'bg-gradient-to-br from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Always Here to Help',
      color: 'bg-gradient-to-br from-orange-500 to-red-500'
    }
  ];

  // Falling fruits configuration - Memoized for performance
  const fallingFruits = useMemo(() => {
    const fruits = [
      '🍓', '🍇', '🍊', '🍋', '🍌', '🍉',
      '🍑', '🍒', '🍎', '🍐', '🥝', '🍍',
    ];
    
    const isMobile = window.innerWidth < 768;
    const fruitCount = isMobile ? 8 : 15;
    
    return [...Array(fruitCount)].map((_, i) => ({
      fruit: fruits[Math.floor(Math.random() * fruits.length)],
      left: Math.random() * 100,
      duration: 8 + Math.random() * 8,
      delay: Math.random() * 5,
      size: isMobile ? 15 + Math.random() * 15 : 20 + Math.random() * 20,
      rotation: Math.random() * 360,
      swing: 50 + Math.random() * 100,
    }));
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <Header />
      
      {/* Falling Fruits Animation - Optimized */}
      <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
        {fallingFruits.map((item, i) => (
          <div
            key={i}
            className="absolute animate-fall-fruit"
            style={{
              left: `${item.left}%`,
              top: '-100px',
              fontSize: `${item.size}px`,
              animationDuration: `${item.duration}s`,
              animationDelay: `${item.delay}s`,
              transform: `rotate(${item.rotation}deg)`,
              '--swing-amount': `${item.swing}px`,
            } as React.CSSProperties}
          >
            {item.fruit}
          </div>
        ))}
      </div>
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Premium Background with Gradient Overlay - Optimized Parallax */}
          <div className="absolute inset-0 will-change-transform">
            <div 
              className="absolute inset-0 scale-110"
              style={{ 
                transform: `translate3d(0, ${scrollY * 0.2}px, 0)`,
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                perspective: 1000
              }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Luxurious%20Persian%20spices%20and%20dried%20fruits%20arranged%20artistically%20on%20elegant%20marble%20surface%2C%20premium%20food%20photography%2C%20warm%20golden%20lighting%2C%20sophisticated%20composition%20with%20saffron%20threads%2C%20pistachios%2C%20and%20traditional%20Persian%20elements%2C%20high-end%20commercial%20style&width=1920&height=1080&seq=hero1&orientation=landscape"
                alt="Premium Persian Delicacies"
                className="w-full h-full object-cover object-center"
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-emerald-900/50 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>
          </div>

          {/* Floating Particles - Reduced for performance */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${8 + Math.random() * 12}s`
                }}
              />
            ))}
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center pt-24 md:pt-32">
            <div className="animate-slide-down">
              <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full mb-6 md:mb-8 shadow-2xl hover:bg-white/20 transition-all duration-300">
                <i className="ri-star-fill text-yellow-400 text-lg md:text-xl animate-pulse"></i>
                <span className="text-white font-semibold text-xs md:text-sm lg:text-base">Premium Quality Since 2023</span>
                <i className="ri-star-fill text-yellow-400 text-lg md:text-xl animate-pulse"></i>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 md:mb-6 animate-slide-up drop-shadow-2xl px-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t('heroTitle')}
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-100 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in drop-shadow-lg px-4">
              {t('heroDescription')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 lg:gap-6 animate-scale-in px-4">
              <Link
                to="/products"
                className="w-full sm:w-auto group px-6 md:px-8 lg:px-10 py-3 md:py-4 lg:py-5 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-teal-700 text-white rounded-xl md:rounded-2xl font-bold text-base md:text-lg transition-all duration-300 shadow-2xl shadow-emerald-500/50 hover:shadow-emerald-500/70 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 md:gap-3"
              >
                <i className="ri-shopping-bag-3-line text-xl md:text-2xl group-hover:scale-110 transition-transform"></i>
                {t('shopNow')}
                <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-2 transition-transform"></i>
              </Link>
              
              <Link
                to="/about"
                className="w-full sm:w-auto group px-6 md:px-8 lg:px-10 py-3 md:py-4 lg:py-5 bg-white/10 backdrop-blur-xl border-2 border-white/30 hover:bg-white/20 text-white rounded-xl md:rounded-2xl font-bold text-base md:text-lg transition-all duration-300 shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 md:gap-3"
              >
                <i className="ri-information-line text-xl md:text-2xl group-hover:scale-110 transition-transform"></i>
                {t('learnMore')}
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 md:mt-16 grid grid-cols-2 md:flex md:flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8 xl:gap-12 animate-fade-in px-4">
              {[
                { icon: 'ri-shield-check-line', text: '100% Authentic', color: 'from-emerald-400 to-teal-400' },
                { icon: 'ri-truck-line', text: 'Free Shipping', color: 'from-blue-400 to-cyan-400' },
                { icon: 'ri-award-line', text: 'Premium Quality', color: 'from-yellow-400 to-orange-400' },
                { icon: 'ri-customer-service-2-line', text: '24/7 Support', color: 'from-purple-400 to-pink-400' }
              ].map((item, index) => (
                <div key={index} className="flex flex-col sm:flex-row items-center gap-2 md:gap-3 group hover:scale-110 transition-transform duration-300">
                  <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br ${item.color} rounded-lg md:rounded-xl flex items-center justify-center shadow-lg group-hover:rotate-12 transition-transform duration-300`}>
                    <i className={`${item.icon} text-white text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-white font-semibold text-xs sm:text-sm md:text-base text-center sm:text-left">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll Indicator - Hidden on mobile */}
          <div className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
              <div className="w-1.5 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('exploreCategories')}
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
                {t('categoriesDescription')}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 lg:gap-8">
              {[
                { 
                  name: t('nuts'), 
                  icon: 'ri-plant-line', 
                  image: 'https://readdy.ai/api/search-image?query=Premium%20mixed%20nuts%20walnuts%20almonds%20pistachios%20hazelnuts%20cashews%20in%20elegant%20wooden%20bowl%20on%20pristine%20white%20marble%20surface%20with%20soft%20natural%20window%20lighting%20professional%20food%20photography%20minimalist%20composition%20high%20quality%20organic%20nuts%20warm%20golden%20tones&width=400&height=400&seq=categorynutsunique2025a&orientation=squarish',
                  link: '/products?category=nuts'
                },
                { 
                  name: t('driedFruits'), 
                  icon: 'ri-leaf-line', 
                  image: 'https://readdy.ai/api/search-image?query=Colorful%20assorted%20dried%20fruits%20mango%20slices%20peaches%20plums%20figs%20apricots%20arranged%20beautifully%20on%20white%20ceramic%20plate%20clean%20background%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20style%20premium%20quality%20vibrant%20colors&width=400&height=400&seq=categorydriedunique2025b&orientation=squarish',
                  link: '/products?category=dried-fruits'
                },
                { 
                  name: t('sweets'), 
                  icon: 'ri-cake-3-line', 
                  image: 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20sweets%20gaz%20sohan%20pashmak%20latifeh%20arranged%20elegantly%20on%20white%20marble%20surface%20with%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20composition%20premium%20quality%20Middle%20Eastern%20confectionery%20golden%20tones&width=400&height=400&seq=categorysweetsunique2025c&orientation=squarish',
                  link: '/products?category=sweets'
                },
                { 
                  name: t('pickles'), 
                  icon: 'ri-flask-line', 
                  image: 'https://readdy.ai/api/search-image?query=Artisan%20pickled%20vegetables%20olives%20pearl%20garlic%20in%20elegant%20glass%20jars%20on%20clean%20white%20background%20with%20fresh%20herbs%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20style%20premium%20quality%20preserved%20foods&width=400&height=400&seq=categorypicklesunique2025d&orientation=squarish',
                  link: '/products?category=pickles'
                },
                { 
                  name: t('jams'), 
                  icon: 'ri-drop-line', 
                  image: 'https://readdy.ai/api/search-image?query=Premium%20fruit%20jams%20strawberry%20carrot%20blackcurrant%20honey%20in%20elegant%20glass%20jars%20with%20fresh%20fruits%20on%20white%20marble%20surface%20soft%20natural%20lighting%20professional%20food%20photography%20minimalist%20composition%20high%20quality%20preserves%20bright%20colors&width=400&height=400&seq=categoryjamsunique2025e&orientation=squarish',
                  link: '/products?category=jams'
                },
              ].map((category, index) => (
                <Link
                  key={index}
                  to={category.link}
                  className="group relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-gray-50 to-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                >
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4 md:p-6">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full mb-3 md:mb-4 shadow-xl transform transition-all duration-500 group-hover:scale-110 group-hover:bg-emerald-600">
                      <i className={`${category.icon} text-2xl md:text-3xl text-emerald-600 group-hover:text-white transition-colors duration-500`}></i>
                    </div>
                    <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 text-center drop-shadow-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {category.name}
                    </h3>
                    <span className="mt-2 md:mt-3 text-xs md:text-sm font-semibold text-emerald-600 group-hover:text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0 whitespace-nowrap">
                      {t('shopNow')} →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Testimonials Section */}
        <section className="relative py-16 md:py-20 lg:py-24 px-4 md:px-6 bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, #10b981 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }}></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('whatCustomersSay')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                {t('testimonialDescription')}
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-8 md:mb-12 lg:mb-16">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className="group bg-white rounded-xl md:rounded-2xl lg:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-4 md:p-6 lg:p-8 hover:-translate-y-2"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Rating */}
                  <div className="flex items-center gap-0.5 md:gap-1 mb-3 md:mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm md:text-base lg:text-lg"></i>
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed mb-4 md:mb-6 line-clamp-4 md:line-clamp-none">
                    "{testimonial.review}"
                  </p>

                  {/* Customer Info */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100 flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-bold text-gray-900 text-xs sm:text-sm md:text-base truncate">
                        {testimonial.name}
                      </h4>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">
                        {testimonial.location}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-checkbox-circle-fill text-emerald-600 text-[10px] md:text-xs"></i>
                        <span className="text-[10px] md:text-xs text-emerald-600 font-semibold">
                          {t('verifiedPurchase')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Product Purchased */}
                  <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-100">
                    <p className="text-[10px] md:text-xs text-gray-500 truncate">
                      {t('purchased')}: <span className="font-semibold text-gray-700">{testimonial.product}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              {trustBadges.map((badge, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg md:rounded-xl lg:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-3 md:p-4 lg:p-6 text-center hover:-translate-y-1"
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 mx-auto mb-2 md:mb-3 lg:mb-4 rounded-full ${badge.color} flex items-center justify-center`}>
                    <i className={`${badge.icon} text-lg md:text-2xl lg:text-3xl text-white`}></i>
                  </div>
                  <h4 className="font-bold text-gray-900 text-xs md:text-sm lg:text-base mb-1 md:mb-2">
                    {badge.title}
                  </h4>
                  <p className="text-[10px] md:text-xs lg:text-sm text-gray-600">
                    {badge.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}